import { CallbackType as b } from "../auth/enums.js";
import { WebAuthnStepType as d, WebAuthnOutcomeType as s, WebAuthnOutcome as g } from "./enums.js";
import { arrayBufferToString as h, parseRelyingPartyId as f, parseCredentials as C, parsePubKeyArray as A } from "./helpers.js";
import { parseWebAuthnAuthenticateText as E, parseWebAuthnRegisterText as P } from "./script-parser.js";
class T {
  /**
   * Determines if the given step is a WebAuthn step.
   *
   * @param step The step to evaluate
   * @return A WebAuthnStepType value
   */
  static getWebAuthnStepType(e) {
    const a = this.getOutcomeCallback(e), t = this.getMetadataCallback(e), i = this.getTextOutputCallback(e);
    if (a && t) {
      const n = t.getOutputValue("data");
      return n != null && n.pubKeyCredParams ? d.Registration : d.Authentication;
    } else
      return a && i ? i.getMessage().includes("pubKeyCredParams") ? d.Registration : d.Authentication : d.None;
  }
  /**
   * Populates the step with the necessary authentication outcome.
   *
   * @param step The step that contains WebAuthn authentication data
   * @return The populated step
   */
  static async authenticate(e) {
    const { hiddenCallback: a, metadataCallback: t, textOutputCallback: i } = this.getCallbacks(e);
    if (a && (t || i)) {
      let n;
      try {
        let r;
        if (t) {
          const c = t.getOutputValue("data");
          r = this.createAuthenticationPublicKey(c);
        } else
          i && (r = E(i.getMessage()));
        const o = await this.getAuthenticationCredential(r);
        n = this.getAuthenticationOutcome(o);
      } catch (r) {
        throw r instanceof Error ? r.name === s.NotSupportedError ? (a.setInputValue(g.Unsupported), r) : (a.setInputValue(`${g.Error}::${r.name}:${r.message}`), r) : r;
      }
      return a.setInputValue(n), e;
    } else {
      const n = new Error("Incorrect callbacks for WebAuthn authentication");
      throw n.name = s.DataError, a == null || a.setInputValue(`${g.Error}::${n.name}:${n.message}`), n;
    }
  }
  /**
   * Populates the step with the necessary registration outcome.
   *
   * @param step The step that contains WebAuthn registration data
   * @return The populated step
   */
  // Can make this generic const in Typescript 5.0 > and the name itself will
  // be inferred from the type so `typeof deviceName` will not just return string
  // but the actual name of the deviceName passed in as a generic.
  static async register(e, a) {
    const { hiddenCallback: t, metadataCallback: i, textOutputCallback: n } = this.getCallbacks(e);
    if (t && (i || n)) {
      let r;
      try {
        let o;
        if (i) {
          const c = i.getOutputValue("data");
          o = this.createRegistrationPublicKey(c);
          const l = await this.getRegistrationCredential(
            o
          );
          r = this.getRegistrationOutcome(l);
        } else if (n) {
          o = P(n.getMessage());
          const c = await this.getRegistrationCredential(
            o
          );
          r = this.getRegistrationOutcome(c);
        } else
          throw new Error("No Credential found from Public Key");
      } catch (o) {
        throw o instanceof Error ? o.name === s.NotSupportedError ? (t.setInputValue(g.Unsupported), o) : (t.setInputValue(`${g.Error}::${o.name}:${o.message}`), o) : o;
      }
      return t.setInputValue(
        a && a.length > 0 ? `${r}::${a}` : r
      ), e;
    } else {
      const r = new Error("Incorrect callbacks for WebAuthn registration");
      throw r.name = s.DataError, t == null || t.setInputValue(`${g.Error}::${r.name}:${r.message}`), r;
    }
  }
  /**
   * Returns an object containing the two WebAuthn callbacks.
   *
   * @param step The step that contains WebAuthn callbacks
   * @return The WebAuthn callbacks
   */
  static getCallbacks(e) {
    const a = this.getOutcomeCallback(e), t = this.getMetadataCallback(e), i = this.getTextOutputCallback(e), n = {
      hiddenCallback: a
    };
    return t ? n.metadataCallback = t : i && (n.textOutputCallback = i), n;
  }
  /**
   * Returns the WebAuthn metadata callback containing data to pass to the browser
   * Web Authentication API.
   *
   * @param step The step that contains WebAuthn callbacks
   * @return The metadata callback
   */
  static getMetadataCallback(e) {
    return e.getCallbacksOfType(b.MetadataCallback).find((a) => {
      const t = a.getOutputByName("data", void 0);
      return t && t.hasOwnProperty("relyingPartyId");
    });
  }
  /**
   * Returns the WebAuthn hidden value callback where the outcome should be populated.
   *
   * @param step The step that contains WebAuthn callbacks
   * @return The hidden value callback
   */
  static getOutcomeCallback(e) {
    return e.getCallbacksOfType(b.HiddenValueCallback).find((a) => a.getOutputByName("id", "") === "webAuthnOutcome");
  }
  /**
   * Returns the WebAuthn metadata callback containing data to pass to the browser
   * Web Authentication API.
   *
   * @param step The step that contains WebAuthn callbacks
   * @return The metadata callback
   */
  static getTextOutputCallback(e) {
    return e.getCallbacksOfType(b.TextOutputCallback).find((a) => {
      const t = a.getOutputByName("message", void 0);
      return t && t.includes("webAuthnOutcome");
    });
  }
  /**
   * Retrieves the credential from the browser Web Authentication API.
   *
   * @param options The public key options associated with the request
   * @return The credential
   */
  static async getAuthenticationCredential(e) {
    if (!window.PublicKeyCredential) {
      const t = new Error("PublicKeyCredential not supported by this browser");
      throw t.name = s.NotSupportedError, t;
    }
    return await navigator.credentials.get({ publicKey: e });
  }
  /**
   * Converts an authentication credential into the outcome expected by OpenAM.
   *
   * @param credential The credential to convert
   * @return The outcome string
   */
  static getAuthenticationOutcome(e) {
    if (e === null) {
      const a = new Error("No credential generated from authentication");
      throw a.name = s.UnknownError, a;
    }
    try {
      const a = h(e.response.clientDataJSON), t = e.response, i = new Int8Array(
        t.authenticatorData
      ).toString(), n = new Int8Array(t.signature).toString(), r = h(e.response.userHandle);
      let o = `${a}::${i}::${n}::${e.id}`;
      return r && (o = `${o}::${r}`), o;
    } catch {
      const t = new Error("Transforming credential object to string failed");
      throw t.name = s.EncodingError, t;
    }
  }
  /**
   * Retrieves the credential from the browser Web Authentication API.
   *
   * @param options The public key options associated with the request
   * @return The credential
   */
  static async getRegistrationCredential(e) {
    if (!window.PublicKeyCredential) {
      const t = new Error("PublicKeyCredential not supported by this browser");
      throw t.name = s.NotSupportedError, t;
    }
    return await navigator.credentials.create({
      publicKey: e
    });
  }
  /**
   * Converts a registration credential into the outcome expected by OpenAM.
   *
   * @param credential The credential to convert
   * @return The outcome string
   */
  static getRegistrationOutcome(e) {
    if (e === null) {
      const a = new Error("No credential generated from registration");
      throw a.name = s.UnknownError, a;
    }
    try {
      const a = h(e.response.clientDataJSON), t = e.response, i = new Int8Array(
        t.attestationObject
      ).toString();
      return `${a}::${i}::${e.id}`;
    } catch {
      const t = new Error("Transforming credential object to string failed");
      throw t.name = s.EncodingError, t;
    }
  }
  /**
   * Converts authentication tree metadata into options required by the browser
   * Web Authentication API.
   *
   * @param metadata The metadata provided in the authentication tree MetadataCallback
   * @return The Web Authentication API request options
   */
  static createAuthenticationPublicKey(e) {
    const {
      acceptableCredentials: a,
      allowCredentials: t,
      challenge: i,
      relyingPartyId: n,
      timeout: r,
      userVerification: o
    } = e, c = f(n), l = C(t || a || "");
    return {
      challenge: Uint8Array.from(atob(i), (p) => p.charCodeAt(0)).buffer,
      timeout: r,
      // only add key-value pair if proper value is provided
      ...l && { allowCredentials: l },
      ...o && { userVerification: o },
      ...c && { rpId: c }
    };
  }
  /**
   * Converts authentication tree metadata into options required by the browser
   * Web Authentication API.
   *
   * @param metadata The metadata provided in the authentication tree MetadataCallback
   * @return The Web Authentication API request options
   */
  static createRegistrationPublicKey(e) {
    const { pubKeyCredParams: a } = e, t = A(a);
    if (!t) {
      const u = new Error("Missing pubKeyCredParams property from registration options");
      throw u.name = s.DataError, u;
    }
    const i = C(e.excludeCredentials), {
      attestationPreference: n,
      authenticatorSelection: r,
      challenge: o,
      relyingPartyId: c,
      relyingPartyName: l,
      timeout: p,
      userId: w,
      userName: m,
      displayName: O
    } = e, y = f(c), k = {
      name: l,
      ...y && { id: y }
    };
    return {
      attestation: n,
      authenticatorSelection: JSON.parse(r),
      challenge: Uint8Array.from(atob(o), (u) => u.charCodeAt(0)).buffer,
      ...i.length && { excludeCredentials: i },
      pubKeyCredParams: t,
      rp: k,
      timeout: p,
      user: {
        displayName: O || m,
        id: Int8Array.from(w.split("").map((u) => u.charCodeAt(0))),
        name: m
      }
    };
  }
}
export {
  g as WebAuthnOutcome,
  d as WebAuthnStepType,
  T as default
};
